// Mock Budget Data for Access Project Management
import type {
  ProjectBudgetData,
  BudgetSummary,
  CostBudget,
  SalesBudget,
  ResourceBudget,
  AllBudgetsView,
  BudgetAlert,
  BudgetRevision,
  BurnRateDataPoint,
} from '@/types/budget.types';
import { mockGlobalCategories, mockGlobalSubcategories } from './category-mock-data';

// ============================================================================
// PROJECT 1: FIXED PRICE - Website Redesign (Amber - Cost Warning)
// ============================================================================

const fixedPriceProject: ProjectBudgetData = {
  projectId: 'p1',
  projectName: 'Website Redesign',
  projectType: 'fixed-price',
  
  summary: {
    revenue: {
      budget: 100000,
      actual: 45000,
      variance: 55000,
      variancePercent: 55,
      progressPercent: 45,
      trend: 'up',
      status: 'green',
    },
    costs: {
      budget: 93500,
      actual: 47000,
      variance: 46500,
      variancePercent: 49.7,
      progressPercent: 50.3,
      trend: 'up',
      status: 'amber',
    },
    margin: {
      budgetedMargin: 40.0,
      actualMargin: 27.8,
      variance: -12.2,
      status: 'amber',
    },
    hours: {
      budget: 600,
      actual: 310,
      remaining: 290,
      progressPercent: 51.7,
      status: 'amber',
    },
    health: {
      score: 'amber',
      statusText: 'Requires Attention',
      factors: [
        'Cost budget 54% consumed with 45% of work remaining',
        'Margin has decreased from 40% to 27.8%',
        'Additional design hours impacting profitability',
      ],
    },
  },
  
  costBudget: {
    projectId: 'p1',
    categories: [
      {
        id: 'cat1',
        name: 'Consulting',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-1',
        budget: 40000,
        actual: 23000,
        committed: 0,
        available: 17000,
        progressPercent: 57.5,
        subcategories: [
          {
            id: 'sub1',
            name: 'Strategy Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-1',
            budget: 20000,
            actual: 13000,
            committed: 0,
            available: 7000,
            progressPercent: 65.0,
          },
          {
            id: 'sub2',
            name: 'Technical Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-2',
            budget: 15000,
            actual: 8000,
            committed: 0,
            available: 7000,
            progressPercent: 53.3,
          },
          {
            id: 'sub3',
            name: 'Business Analysis',
            scope: 'global',
            globalSubcategoryId: 'gs-3',
            budget: 5000,
            actual: 2000,
            committed: 0,
            available: 3000,
            progressPercent: 40.0,
          },
        ],
      },
      {
        id: 'cat2',
        name: 'Development',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-2',
        budget: 15000,
        actual: 8000,
        committed: 0,
        available: 7000,
        progressPercent: 53.3,
        subcategories: [
          {
            id: 'sub4',
            name: 'Frontend Development',
            scope: 'global',
            globalSubcategoryId: 'gs-4',
            budget: 8000,
            actual: 4500,
            committed: 0,
            available: 3500,
            progressPercent: 56.3,
          },
          {
            id: 'sub5',
            name: 'Backend Development',
            scope: 'global',
            globalSubcategoryId: 'gs-5',
            budget: 7000,
            actual: 3500,
            committed: 0,
            available: 3500,
            progressPercent: 50.0,
          },
        ],
      },
      {
        id: 'cat3',
        name: 'Design',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-3',
        budget: 12000,
        actual: 6000,
        committed: 0,
        available: 6000,
        progressPercent: 50.0,
        subcategories: [
          {
            id: 'sub6',
            name: 'UI Design',
            scope: 'global',
            globalSubcategoryId: 'gs-8',
            budget: 8000,
            actual: 4000,
            committed: 0,
            available: 4000,
            progressPercent: 50.0,
          },
          {
            id: 'sub7',
            name: 'UX Research',
            scope: 'global',
            globalSubcategoryId: 'gs-9',
            budget: 4000,
            actual: 2000,
            committed: 0,
            available: 2000,
            progressPercent: 50.0,
          },
        ],
      },
      {
        id: 'cat4',
        name: 'Travel',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-6',
        budget: 8000,
        actual: 2500,
        committed: 0,
        available: 5500,
        progressPercent: 31.3,
        subcategories: [
          {
            id: 'sub8',
            name: 'Flights',
            scope: 'global',
            globalSubcategoryId: 'gs-11',
            budget: 5000,
            actual: 1800,
            committed: 0,
            available: 3200,
            progressPercent: 36.0,
          },
          {
            id: 'sub9',
            name: 'Ground Transport',
            scope: 'global',
            globalSubcategoryId: 'gs-12',
            budget: 3000,
            actual: 700,
            committed: 0,
            available: 2300,
            progressPercent: 23.3,
          },
        ],
      },
      {
        id: 'cat5',
        name: 'Software & Tools',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-8',
        budget: 5000,
        actual: 2000,
        committed: 0,
        available: 3000,
        progressPercent: 40.0,
        subcategories: [
          {
            id: 'sub10',
            name: 'Development Tools',
            scope: 'global',
            globalSubcategoryId: 'gs-14',
            budget: 2000,
            actual: 800,
            committed: 0,
            available: 1200,
            progressPercent: 40.0,
          },
          {
            id: 'sub11',
            name: 'Cloud Services',
            scope: 'global',
            globalSubcategoryId: 'gs-16',
            budget: 3000,
            actual: 1200,
            committed: 0,
            available: 1800,
            progressPercent: 40.0,
          },
        ],
      },
      {
        id: 'cat6',
        name: 'Technical Subcontractors',
        type: 'subcontractors',
        scope: 'global',
        globalCategoryId: 'gc-11',
        budget: 7000,
        actual: 1500,
        committed: 3000,
        available: 2500,
        progressPercent: 64.3,
      },
      {
        id: 'cat7',
        name: 'Office Overhead',
        type: 'overhead',
        scope: 'global',
        globalCategoryId: 'gc-13',
        budget: 3000,
        actual: 1500,
        committed: 0,
        available: 1500,
        progressPercent: 50.0,
      },
      {
        id: 'cat8',
        name: 'Risk Reserve',
        type: 'contingency',
        scope: 'global',
        globalCategoryId: 'gc-15',
        budget: 2000,
        actual: 0,
        committed: 0,
        available: 2000,
        progressPercent: 0,
      },
      {
        id: 'cat9',
        name: 'Custom Website Hosting',
        type: 'expenses',
        scope: 'project',
        budget: 1500,
        actual: 500,
        committed: 0,
        available: 1000,
        progressPercent: 33.3,
      },
    ],
    phases: [
      {
        id: 'phase1',
        phaseId: 'ph1',
        phaseName: 'Discovery',
        budget: 10000,
        actual: 9800,
        variance: 200,
        status: 'completed',
      },
      {
        id: 'phase2',
        phaseId: 'ph2',
        phaseName: 'Design',
        budget: 15000,
        actual: 14200,
        variance: 800,
        status: 'completed',
      },
      {
        id: 'phase3',
        phaseId: 'ph3',
        phaseName: 'Development',
        budget: 25000,
        actual: 8500,
        variance: 16500,
        status: 'in-progress',
      },
      {
        id: 'phase4',
        phaseId: 'ph4',
        phaseName: 'Testing',
        budget: 7000,
        actual: 0,
        variance: 7000,
        status: 'not-started',
      },
      {
        id: 'phase5',
        phaseId: 'ph5',
        phaseName: 'Deployment',
        budget: 3000,
        actual: 0,
        variance: 3000,
        status: 'not-started',
      },
    ],
    totals: {
      budget: 60000,
      actual: 32500,
      committed: 3000,
      available: 24500,
      progressPercent: 59.2,
    },
  },
  
  salesBudget: {
    projectId: 'p1',
    projectType: 'fixed-price',
    contractValue: 100000,
    invoiced: 60000,
    recognised: 45000,
    remaining: 55000,
    milestones: [
      {
        id: 'ms1',
        name: 'Project Kickoff',
        amount: 10000,
        dueDate: '2024-01-15',
        invoiced: true,
        invoiceDate: '2024-01-15',
        paid: true,
        paidDate: '2024-01-30',
        status: 'paid',
      },
      {
        id: 'ms2',
        name: 'Design Approval',
        amount: 20000,
        dueDate: '2024-02-28',
        invoiced: true,
        invoiceDate: '2024-02-28',
        paid: true,
        paidDate: '2024-03-15',
        status: 'paid',
      },
      {
        id: 'ms3',
        name: 'UAT Sign-off',
        amount: 30000,
        dueDate: '2024-03-31',
        invoiced: true,
        invoiceDate: '2024-03-31',
        paid: false,
        status: 'due',
      },
      {
        id: 'ms4',
        name: 'Go-Live',
        amount: 30000,
        dueDate: '2024-04-30',
        invoiced: false,
        paid: false,
        status: 'pending',
      },
      {
        id: 'ms5',
        name: 'Warranty End',
        amount: 10000,
        dueDate: '2024-07-31',
        invoiced: false,
        paid: false,
        status: 'future',
      },
    ],
    revenueRecognition: {
      method: 'percentage-of-completion',
      percentComplete: 45,
      revenueToRecognise: 45000,
      revenueRecognised: 45000,
      deferredRevenue: 15000,
      unbilledRevenue: 0,
    },
    phaseRevenue: [
      {
        id: 'pr1',
        phaseId: 'ph1',
        phaseName: 'Discovery',
        budget: 15000,
        actual: 15000,
        variance: 0,
        status: 'completed',
      },
      {
        id: 'pr2',
        phaseId: 'ph2',
        phaseName: 'Design',
        budget: 25000,
        actual: 23000,
        variance: 2000,
        status: 'completed',
      },
      {
        id: 'pr3',
        phaseId: 'ph3',
        phaseName: 'Development',
        budget: 40000,
        actual: 7000,
        variance: 33000,
        status: 'in-progress',
      },
      {
        id: 'pr4',
        phaseId: 'ph4',
        phaseName: 'Testing',
        budget: 15000,
        actual: 0,
        variance: 15000,
        status: 'not-started',
      },
      {
        id: 'pr5',
        phaseId: 'ph5',
        phaseName: 'Deployment',
        budget: 5000,
        actual: 0,
        variance: 5000,
        status: 'not-started',
      },
    ],
  },
  
  resourceBudget: {
    projectId: 'p1',
    summary: {
      totalBudget: 600,
      totalActual: 482, // Updated: 110 + 135 + 65 + 120 + 42 + 10 = 80.3% (WARNING)
      totalRemaining: 118,
      progressPercent: 80.3,
      billablePercent: 82.3,
      targetBillablePercent: 85.0,
      totalBudgetCost: 93500,
      totalActualCost: 70250, // Updated: 22000 + 20250 + 7800 + 15000 + 4200 + 1000
    },
    byRole: [
      {
        id: 'role1',
        role: 'Principal Consultant',
        budgetHours: 100,
        actualHours: 110, // OVER STATUS - 110% used
        remainingHours: -10,
        percentUsed: 110,
        billRate: 200,
        budgetCost: 20000,
        actualCost: 22000,
      },
      {
        id: 'role2',
        role: 'Senior Consultant',
        budgetHours: 150,
        actualHours: 135, // WARNING STATUS - 90% used
        remainingHours: 15,
        percentUsed: 90,
        billRate: 150,
        budgetCost: 22500,
        actualCost: 20250,
      },
      {
        id: 'role3',
        role: 'Consultant',
        budgetHours: 100,
        actualHours: 65, // GOOD STATUS - 65% used
        remainingHours: 35,
        percentUsed: 65,
        billRate: 120,
        budgetCost: 12000,
        actualCost: 7800,
      },
      {
        id: 'role4',
        role: 'Senior Developer',
        budgetHours: 150,
        actualHours: 120, // WARNING STATUS - 80% used
        remainingHours: 30,
        percentUsed: 80,
        billRate: 125,
        budgetCost: 18750,
        actualCost: 15000,
      },
      {
        id: 'role5',
        role: 'Developer',
        budgetHours: 50,
        actualHours: 42, // WARNING STATUS - 84% used
        remainingHours: 8,
        percentUsed: 84,
        billRate: 100,
        budgetCost: 5000,
        actualCost: 4200,
      },
      {
        id: 'role6',
        role: 'Designer',
        budgetHours: 50,
        actualHours: 10, // GOOD STATUS - 20% used
        remainingHours: 40,
        percentUsed: 20,
        billRate: 100,
        budgetCost: 5000,
        actualCost: 1000,
      },
    ],
    byPerson: [
      {
        id: 'person1',
        personId: 'e1',
        personName: 'Sarah Jones',
        role: 'Principal Consultant',
        budgetHours: 80,
        actualHours: 65,
        utilisation: 81.3,
        costRate: 150,
        budgetCost: 12000,
        actualCost: 9750,
      },
      {
        id: 'person2',
        personId: 'e2',
        personName: 'James Smith',
        role: 'Senior Consultant',
        budgetHours: 100,
        actualHours: 75,
        utilisation: 75.0,
        costRate: 120,
        budgetCost: 12000,
        actualCost: 9000,
      },
      {
        id: 'person3',
        personId: 'e3',
        personName: 'Emma Williams',
        role: 'Senior Consultant',
        budgetHours: 50,
        actualHours: 15,
        utilisation: 30.0,
        costRate: 120,
        budgetCost: 6000,
        actualCost: 1800,
      },
      {
        id: 'person4',
        personId: 'e4',
        personName: 'Michael Brown',
        role: 'Senior Developer',
        budgetHours: 100,
        actualHours: 50,
        utilisation: 50.0,
        costRate: 100,
        budgetCost: 10000,
        actualCost: 5000,
      },
      {
        id: 'person5',
        personId: 'e5',
        personName: 'Lisa Taylor',
        role: 'Developer',
        budgetHours: 50,
        actualHours: 20,
        utilisation: 40.0,
        costRate: 80,
        budgetCost: 4000,
        actualCost: 1600,
      },
      {
        id: 'person6',
        personId: 'e6',
        personName: 'David Wilson',
        role: 'Designer',
        budgetHours: 50,
        actualHours: 10,
        utilisation: 20.0,
        costRate: 80,
        budgetCost: 4000,
        actualCost: 800,
      },
      {
        id: 'person7',
        personId: 'unassigned1',
        personName: '(Unassigned)',
        role: 'Consultant',
        budgetHours: 100,
        actualHours: 40,
        utilisation: 40.0,
        costRate: 100,
        budgetCost: 10000,
        actualCost: 4000,
      },
      {
        id: 'person8',
        personId: 'unassigned2',
        personName: '(Unassigned)',
        role: 'Principal Consultant',
        budgetHours: 20,
        actualHours: 15,
        utilisation: 75.0,
        costRate: 150,
        budgetCost: 3000,
        actualCost: 2250,
      },
    ],
    byPhase: [
      {
        phaseId: 'ph1',
        phaseName: 'Discovery',
        budgetHours: 80,
        actualHours: 78,
        remainingHours: 2,
        status: 'complete',
        startDate: '2024-01-01',
        endDate: '2024-01-31',
      },
      {
        phaseId: 'ph2',
        phaseName: 'Design',
        budgetHours: 120,
        actualHours: 110,
        remainingHours: 10,
        status: 'in-progress',
        startDate: '2024-02-01',
        endDate: '2024-03-15',
      },
      {
        phaseId: 'ph3',
        phaseName: 'Development',
        budgetHours: 280,
        actualHours: 122,
        remainingHours: 158,
        status: 'in-progress',
        startDate: '2024-03-16',
        endDate: '2024-06-30',
      },
      {
        phaseId: 'ph4',
        phaseName: 'Testing',
        budgetHours: 80,
        actualHours: 0,
        remainingHours: 80,
        status: 'not-started',
        startDate: '2024-07-01',
        endDate: '2024-07-31',
      },
      {
        phaseId: 'ph5',
        phaseName: 'Deployment',
        budgetHours: 40,
        actualHours: 0,
        remainingHours: 40,
        status: 'not-started',
        startDate: '2024-08-01',
        endDate: '2024-08-15',
      },
    ],
  },
  
  allBudgets: {
    projectId: 'p1',
    profitability: {
      revenue: {
        budget: 100000,
        actual: 45000,
        variance: 55000,
      },
      directCosts: {
        budget: 57000,
        actual: 31000,
        variance: 26000,
      },
      grossProfit: {
        budget: 43000,
        actual: 14000,
        variance: 29000,
      },
      grossMargin: {
        budget: 43.0,
        actual: 31.1,
        variance: -11.9,
      },
      overhead: {
        budget: 3000,
        actual: 1500,
        variance: 1500,
      },
      netProfit: {
        budget: 40000,
        actual: 12500,
        variance: 27500,
      },
      netMargin: {
        budget: 40.0,
        actual: 27.8,
        variance: -12.2,
      },
    },
    burnRateData: generateBurnRateData(60000, 32500, 0.45, 6),
    forecast: {
      projectedFinalRevenue: 98000,
      projectedFinalCost: 65000,
      projectedFinalMargin: 33.7,
      revenueVariance: -2000,
      costVariance: 5000,
      marginVariance: -6.3,
      warningMessage: 'At current rate, project will exceed cost budget by Week 10',
    },
  },
  
  alerts: [
    {
      id: 'alert1',
      projectId: 'p1',
      type: 'cost-warning',
      severity: 'warning',
      title: 'Cost budget 54% consumed',
      description: 'Cost budget 54% consumed with 45% of work remaining. Labour costs tracking 12% over plan.',
      message: 'Cost budget 54% consumed with 45% of work remaining.',
      createdAt: '2024-03-15T10:00:00Z',
      dismissed: false,
    },
    {
      id: 'alert2',
      projectId: 'p1',
      type: 'margin-erosion',
      severity: 'warning',
      title: 'Margin decreased to 27.8%',
      description: 'Margin has decreased from 40% to 27.8% (-12.2%). Primarily due to additional design hours.',
      message: 'Margin has decreased from 40% to 27.8% (-12.2%).',
      createdAt: '2024-03-14T14:30:00Z',
      dismissed: false,
    },
  ],
  
  revisions: [
    {
      id: 'rev3',
      projectId: 'p1',
      revisionNumber: 3,
      date: '2024-03-10',
      changedBy: 'e1',
      changedByName: 'Sarah Jones',
      reason: 'Scope change CR-003 - Additional design requirements',
      status: 'current',
      costBudget: 60000,
      salesBudget: 100000,
      resourceBudget: 600,
      costChange: 5000,
      salesChange: 5000,
      resourceChange: 50,
    },
    {
      id: 'rev2',
      projectId: 'p1',
      revisionNumber: 2,
      date: '2024-02-15',
      changedBy: 'e1',
      changedByName: 'Sarah Jones',
      reason: 'Phase 2 extension approved',
      status: 'baseline',
      costBudget: 55000,
      salesBudget: 95000,
      resourceBudget: 550,
      costChange: 5000,
      salesChange: 5000,
      resourceChange: 50,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-02-16',
    },
    {
      id: 'rev1',
      projectId: 'p1',
      revisionNumber: 1,
      date: '2024-01-01',
      changedBy: 'e2',
      changedByName: 'James Smith',
      reason: 'Initial budget',
      status: 'baseline',
      costBudget: 50000,
      salesBudget: 90000,
      resourceBudget: 500,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-01-02',
    },
  ],
  
  lastUpdated: '2024-03-15T16:45:00Z',
};

// ============================================================================
// PROJECT 2: TIME & MATERIALS - Digital Transformation (Green - On Track)
// ============================================================================

const timeAndMaterialsProject: ProjectBudgetData = {
  projectId: 'p2',
  projectName: 'Digital Transformation Programme',
  projectType: 'time-and-materials',
  
  summary: {
    revenue: {
      budget: 120000,
      actual: 58000,
      variance: 62000,
      variancePercent: 51.7,
      progressPercent: 48.3,
      trend: 'up',
      status: 'green',
    },
    costs: {
      budget: 85000,
      actual: 38000,
      variance: 47000,
      variancePercent: 55.3,
      progressPercent: 44.7,
      trend: 'up',
      status: 'green',
    },
    margin: {
      budgetedMargin: 29.2,
      actualMargin: 34.5,
      variance: 5.3,
      status: 'green',
    },
    hours: {
      budget: 800,
      actual: 380,
      remaining: 420,
      progressPercent: 47.5,
      status: 'green',
    },
    health: {
      score: 'green',
      statusText: 'On Track',
      factors: [
        'Costs tracking below budget',
        'Revenue recognition on schedule',
        'Margin exceeding target by 5.3%',
      ],
    },
  },
  
  costBudget: {
    projectId: 'p2',
    categories: [
      {
        id: 'cat1',
        name: 'Consulting',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-1',
        budget: 35000,
        actual: 18000,
        committed: 0,
        available: 17000,
        progressPercent: 51.4,
        subcategories: [
          {
            id: 'sub1',
            name: 'Strategy Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-1',
            budget: 20000,
            actual: 10000,
            committed: 0,
            available: 10000,
            progressPercent: 50.0,
          },
          {
            id: 'sub2',
            name: 'Technical Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-2',
            budget: 15000,
            actual: 8000,
            committed: 0,
            available: 7000,
            progressPercent: 53.3,
          },
        ],
      },
      {
        id: 'cat2',
        name: 'Development',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-2',
        budget: 25000,
        actual: 10000,
        committed: 0,
        available: 15000,
        progressPercent: 40.0,
        subcategories: [
          {
            id: 'sub3',
            name: 'Frontend Development',
            scope: 'global',
            globalSubcategoryId: 'gs-4',
            budget: 12000,
            actual: 5000,
            committed: 0,
            available: 7000,
            progressPercent: 41.7,
          },
          {
            id: 'sub4',
            name: 'Backend Development',
            scope: 'global',
            globalSubcategoryId: 'gs-5',
            budget: 13000,
            actual: 5000,
            committed: 0,
            available: 8000,
            progressPercent: 38.5,
          },
        ],
      },
      {
        id: 'cat3',
        name: 'Design',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-3',
        budget: 5000,
        actual: 2000,
        committed: 0,
        available: 3000,
        progressPercent: 40.0,
        subcategories: [
          {
            id: 'sub5',
            name: 'UI Design',
            scope: 'global',
            globalSubcategoryId: 'gs-8',
            budget: 5000,
            actual: 2000,
            committed: 0,
            available: 3000,
            progressPercent: 40.0,
          },
        ],
      },
      {
        id: 'cat4',
        name: 'Travel',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-6',
        budget: 7000,
        actual: 3500,
        committed: 0,
        available: 3500,
        progressPercent: 50.0,
        subcategories: [
          {
            id: 'sub6',
            name: 'Flights',
            scope: 'global',
            globalSubcategoryId: 'gs-11',
            budget: 4000,
            actual: 2000,
            committed: 0,
            available: 2000,
            progressPercent: 50.0,
          },
          {
            id: 'sub7',
            name: 'Ground Transport',
            scope: 'global',
            globalSubcategoryId: 'gs-12',
            budget: 3000,
            actual: 1500,
            committed: 0,
            available: 1500,
            progressPercent: 50.0,
          },
        ],
      },
      {
        id: 'cat5',
        name: 'Software & Tools',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-8',
        budget: 4000,
        actual: 1200,
        committed: 0,
        available: 2800,
        progressPercent: 30.0,
        subcategories: [
          {
            id: 'sub8',
            name: 'Development Tools',
            scope: 'global',
            globalSubcategoryId: 'gs-14',
            budget: 2000,
            actual: 600,
            committed: 0,
            available: 1400,
            progressPercent: 30.0,
          },
          {
            id: 'sub9',
            name: 'Cloud Services',
            scope: 'global',
            globalSubcategoryId: 'gs-16',
            budget: 2000,
            actual: 600,
            committed: 0,
            available: 1400,
            progressPercent: 30.0,
          },
        ],
      },
      {
        id: 'cat6',
        name: 'Technical Subcontractors',
        type: 'subcontractors',
        scope: 'global',
        globalCategoryId: 'gc-11',
        budget: 5000,
        actual: 2000,
        committed: 0,
        available: 3000,
        progressPercent: 40.0,
      },
      {
        id: 'cat7',
        name: 'Office Overhead',
        type: 'overhead',
        scope: 'global',
        globalCategoryId: 'gc-13',
        budget: 3000,
        actual: 1000,
        committed: 0,
        available: 2000,
        progressPercent: 33.3,
      },
      {
        id: 'cat8',
        name: 'Custom API Integration',
        type: 'expenses',
        scope: 'project',
        budget: 1000,
        actual: 300,
        committed: 0,
        available: 700,
        progressPercent: 30.0,
      },
    ],
    phases: [
      {
        id: 'phase1',
        phaseId: 'ph1',
        phaseName: 'Assessment',
        budget: 15000,
        actual: 14500,
        variance: 500,
        status: 'completed',
      },
      {
        id: 'phase2',
        phaseId: 'ph2',
        phaseName: 'Planning',
        budget: 20000,
        actual: 18000,
        variance: 2000,
        status: 'completed',
      },
      {
        id: 'phase3',
        phaseId: 'ph3',
        phaseName: 'Implementation',
        budget: 40000,
        actual: 5500,
        variance: 34500,
        status: 'in-progress',
      },
      {
        id: 'phase4',
        phaseId: 'ph4',
        phaseName: 'Optimization',
        budget: 10000,
        actual: 0,
        variance: 10000,
        status: 'not-started',
      },
    ],
    totals: {
      budget: 85000,
      actual: 38000,
      committed: 0,
      available: 47000,
      progressPercent: 44.7,
    },
  },
  
  salesBudget: {
    projectId: 'p2',
    projectType: 'time-and-materials',
    estimated: 120000,
    actual: 58000,
    invoiced: 52000,
    nteAmount: 150000,
    streams: [
      {
        id: 'stream1',
        name: 'Billable Time',
        type: 'billable-time',
        estimated: 105000,
        actual: 52000,
        invoiced: 48000,
        remaining: 53000,
        subcategories: [
          {
            id: 'substream1',
            name: 'Consulting',
            estimated: 60000,
            actual: 32000,
            invoiced: 30000,
            remaining: 28000,
          },
          {
            id: 'substream2',
            name: 'Development',
            estimated: 40000,
            actual: 18000,
            invoiced: 16000,
            remaining: 22000,
          },
          {
            id: 'substream3',
            name: 'Design',
            estimated: 5000,
            actual: 2000,
            invoiced: 2000,
            remaining: 3000,
          },
        ],
      },
      {
        id: 'stream2',
        name: 'Expense Recovery',
        type: 'expense-recovery',
        estimated: 15000,
        actual: 6000,
        invoiced: 4000,
        remaining: 9000,
      },
    ],
    unbilledTime: 4500,
    unbilledExpenses: 500,
    phaseRevenue: [
      {
        id: 'pr1',
        phaseId: 'ph1',
        phaseName: 'Assessment',
        budget: 22000,
        actual: 21500,
        variance: 500,
        status: 'completed',
      },
      {
        id: 'pr2',
        phaseId: 'ph2',
        phaseName: 'Planning',
        budget: 28000,
        actual: 27000,
        variance: 1000,
        status: 'completed',
      },
      {
        id: 'pr3',
        phaseId: 'ph3',
        phaseName: 'Implementation',
        budget: 55000,
        actual: 9500,
        variance: 45500,
        status: 'in-progress',
      },
      {
        id: 'pr4',
        phaseId: 'ph4',
        phaseName: 'Optimization',
        budget: 15000,
        actual: 0,
        variance: 15000,
        status: 'not-started',
      },
    ],
  },
  
  resourceBudget: {
    projectId: 'p2',
    summary: {
      totalBudget: 800,
      totalActual: 380,
      totalRemaining: 420,
      progressPercent: 47.5,
      billablePercent: 88.4,
      targetBillablePercent: 85.0,
      totalBudgetCost: 120000,
      totalActualCost: 57000,
    },
    byRole: [
      {
        id: 'role1',
        role: 'Principal Consultant',
        budgetHours: 150,
        actualHours: 80,
        remainingHours: 70,
        percentUsed: 53.3,
        billRate: 200,
        budgetCost: 30000,
        actualCost: 16000,
      },
      {
        id: 'role2',
        role: 'Senior Consultant',
        budgetHours: 200,
        actualHours: 90,
        remainingHours: 110,
        percentUsed: 45.0,
        billRate: 150,
        budgetCost: 30000,
        actualCost: 13500,
      },
      {
        id: 'role3',
        role: 'Consultant',
        budgetHours: 150,
        actualHours: 40,
        remainingHours: 110,
        percentUsed: 26.7,
        billRate: 120,
        budgetCost: 18000,
        actualCost: 4800,
      },
      {
        id: 'role4',
        role: 'Developer',
        budgetHours: 250,
        actualHours: 120,
        remainingHours: 130,
        percentUsed: 48.0,
        billRate: 125,
        budgetCost: 31250,
        actualCost: 15000,
      },
      {
        id: 'role5',
        role: 'Designer',
        budgetHours: 50,
        actualHours: 20,
        remainingHours: 30,
        percentUsed: 40.0,
        billRate: 100,
        budgetCost: 5000,
        actualCost: 2000,
      },
    ],
    byPerson: [
      {
        id: 'person1',
        personId: 'e1',
        personName: 'Alice Johnson',
        role: 'Principal Consultant',
        budgetHours: 150,
        actualHours: 80,
        utilisation: 53.3,
        costRate: 150,
        budgetCost: 22500,
        actualCost: 12000,
      },
      {
        id: 'person2',
        personId: 'e3',
        personName: 'Catherine Davies',
        role: 'Senior Consultant',
        budgetHours: 120,
        actualHours: 60,
        utilisation: 50.0,
        costRate: 120,
        budgetCost: 14400,
        actualCost: 7200,
      },
      {
        id: 'person3',
        personId: 'e11',
        personName: 'Katie White',
        role: 'Consultant',
        budgetHours: 100,
        actualHours: 40,
        utilisation: 40.0,
        costRate: 100,
        budgetCost: 10000,
        actualCost: 4000,
      },
      {
        id: 'person4',
        personId: 'e4',
        personName: 'David Smith',
        role: 'Developer',
        budgetHours: 150,
        actualHours: 80,
        utilisation: 53.3,
        costRate: 100,
        budgetCost: 15000,
        actualCost: 8000,
      },
      {
        id: 'person5',
        personId: 'e5',
        personName: 'Emma Wilson',
        role: 'Developer',
        budgetHours: 100,
        actualHours: 40,
        utilisation: 40.0,
        costRate: 80,
        budgetCost: 8000,
        actualCost: 3200,
      },
      {
        id: 'person6',
        personId: 'e7',
        personName: 'Grace Taylor',
        role: 'Designer',
        budgetHours: 50,
        actualHours: 20,
        utilisation: 40.0,
        costRate: 80,
        budgetCost: 4000,
        actualCost: 1600,
      },
    ],
    byPhase: [
      {
        phaseId: 'ph1',
        phaseName: 'Assessment',
        budgetHours: 120,
        actualHours: 115,
        remainingHours: 5,
        status: 'complete',
      },
      {
        phaseId: 'ph2',
        phaseName: 'Planning',
        budgetHours: 180,
        actualHours: 170,
        remainingHours: 10,
        status: 'complete',
      },
      {
        phaseId: 'ph3',
        phaseName: 'Implementation',
        budgetHours: 400,
        actualHours: 95,
        remainingHours: 305,
        status: 'in-progress',
      },
      {
        phaseId: 'ph4',
        phaseName: 'Optimization',
        budgetHours: 100,
        actualHours: 0,
        remainingHours: 100,
        status: 'not-started',
      },
    ],
  },
  
  allBudgets: {
    projectId: 'p2',
    profitability: {
      revenue: {
        budget: 120000,
        actual: 58000,
        variance: 62000,
      },
      directCosts: {
        budget: 82000,
        actual: 37000,
        variance: 45000,
      },
      grossProfit: {
        budget: 38000,
        actual: 21000,
        variance: 17000,
      },
      grossMargin: {
        budget: 31.7,
        actual: 36.2,
        variance: 4.5,
      },
      overhead: {
        budget: 3000,
        actual: 1000,
        variance: 2000,
      },
      netProfit: {
        budget: 35000,
        actual: 20000,
        variance: 15000,
      },
      netMargin: {
        budget: 29.2,
        actual: 34.5,
        variance: 5.3,
      },
    },
    burnRateData: generateBurnRateData(85000, 38000, 0.48, 6),
    forecast: {
      projectedFinalRevenue: 122000,
      projectedFinalCost: 82000,
      projectedFinalMargin: 32.8,
      revenueVariance: 2000,
      costVariance: -3000,
      marginVariance: 3.6,
    },
  },
  
  alerts: [],
  
  revisions: [
    {
      id: 'rev1',
      projectId: 'p2',
      revisionNumber: 1,
      date: '2024-01-01',
      changedBy: 'e1',
      changedByName: 'Alice Johnson',
      reason: 'Initial budget',
      status: 'current',
      costBudget: 85000,
      salesBudget: 120000,
      resourceBudget: 800,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-01-02',
    },
  ],
  
  lastUpdated: '2024-03-15T16:45:00Z',
};

// ============================================================================
// PROJECT 3: RETAINER - Ongoing Support (Green - On Track)
// ============================================================================

const retainerProject: ProjectBudgetData = {
  projectId: 'p3',
  projectName: 'Ongoing Support & Maintenance',
  projectType: 'retainer',
  
  summary: {
    revenue: {
      budget: 96000,
      actual: 32350,
      variance: 63650,
      variancePercent: 66.3,
      progressPercent: 33.7,
      trend: 'stable',
      status: 'green',
    },
    costs: {
      budget: 60000,
      actual: 19500,
      variance: 40500,
      variancePercent: 67.5,
      progressPercent: 32.5,
      trend: 'stable',
      status: 'green',
    },
    margin: {
      budgetedMargin: 37.5,
      actualMargin: 39.7,
      variance: 2.2,
      status: 'green',
    },
    hours: {
      budget: 600,
      actual: 183,
      remaining: 417,
      progressPercent: 30.5,
      status: 'green',
    },
    health: {
      score: 'green',
      statusText: 'On Track',
      factors: [
        'Retainer utilization within expected range',
        'Margin exceeding target',
        'Client satisfaction high',
      ],
    },
  },
  
  costBudget: {
    projectId: 'p3',
    categories: [
      {
        id: 'cat1',
        name: 'Consulting',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-1',
        budget: 35000,
        actual: 12000,
        committed: 0,
        available: 23000,
        progressPercent: 34.3,
        subcategories: [
          {
            id: 'sub1',
            name: 'Strategy Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-1',
            budget: 20000,
            actual: 7000,
            committed: 0,
            available: 13000,
            progressPercent: 35.0,
          },
          {
            id: 'sub2',
            name: 'Technical Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-2',
            budget: 15000,
            actual: 5000,
            committed: 0,
            available: 10000,
            progressPercent: 33.3,
          },
        ],
      },
      {
        id: 'cat2',
        name: 'Development',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-2',
        budget: 12000,
        actual: 3500,
        committed: 0,
        available: 8500,
        progressPercent: 29.2,
        subcategories: [
          {
            id: 'sub3',
            name: 'Backend Development',
            scope: 'global',
            globalSubcategoryId: 'gs-5',
            budget: 12000,
            actual: 3500,
            committed: 0,
            available: 8500,
            progressPercent: 29.2,
          },
        ],
      },
      {
        id: 'cat3',
        name: 'Testing & QA',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-4',
        budget: 3000,
        actual: 1000,
        committed: 0,
        available: 2000,
        progressPercent: 33.3,
        subcategories: [
          {
            id: 'sub4',
            name: 'Automated Testing',
            scope: 'global',
            globalSubcategoryId: 'gs-10',
            budget: 3000,
            actual: 1000,
            committed: 0,
            available: 2000,
            progressPercent: 33.3,
          },
        ],
      },
      {
        id: 'cat4',
        name: 'Software & Tools',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-8',
        budget: 6000,
        actual: 2000,
        committed: 0,
        available: 4000,
        progressPercent: 33.3,
        subcategories: [
          {
            id: 'sub5',
            name: 'Development Tools',
            scope: 'global',
            globalSubcategoryId: 'gs-14',
            budget: 3000,
            actual: 1000,
            committed: 0,
            available: 2000,
            progressPercent: 33.3,
          },
          {
            id: 'sub6',
            name: 'Cloud Services',
            scope: 'global',
            globalSubcategoryId: 'gs-16',
            budget: 3000,
            actual: 1000,
            committed: 0,
            available: 2000,
            progressPercent: 33.3,
          },
        ],
      },
      {
        id: 'cat5',
        name: 'Office Overhead',
        type: 'overhead',
        scope: 'global',
        globalCategoryId: 'gc-13',
        budget: 2000,
        actual: 500,
        committed: 0,
        available: 1500,
        progressPercent: 25.0,
      },
      {
        id: 'cat6',
        name: 'Custom Support Tools',
        type: 'expenses',
        scope: 'project',
        budget: 2000,
        actual: 500,
        committed: 0,
        available: 1500,
        progressPercent: 25.0,
      },
    ],
    phases: [],
    totals: {
      budget: 60000,
      actual: 19500,
      committed: 0,
      available: 40500,
      progressPercent: 32.5,
    },
  },
  
  salesBudget: {
    projectId: 'p3',
    projectType: 'retainer',
    retainer: {
      monthlyAmount: 8000,
      contractMonths: 12,
      totalContract: 96000,
      invoiced: 32000,
      remaining: 64000,
      currentMonth: {
        hoursIncluded: 50,
        hoursUsed: 38,
        hoursRemaining: 12,
        overageRate: 175,
        progressPercent: 76,
      },
      rolloverPolicy: 'limited',
      rolloverLimit: 10,
      monthlyBreakdown: [
        {
          month: 'Sep 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 48,
          overageHours: 0,
          overageAmount: 0,
          totalAmount: 8000,
        },
        {
          month: 'Oct 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 52,
          overageHours: 2,
          overageAmount: 350,
          totalAmount: 8350,
        },
        {
          month: 'Nov 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 45,
          overageHours: 0,
          overageAmount: 0,
          totalAmount: 8000,
        },
        {
          month: 'Dec 2024',
          retainerAmount: 8000,
          hoursIncluded: 50,
          hoursUsed: 38,
          overageHours: 0,
          overageAmount: 0,
          totalAmount: 8000,
        },
      ],
    },
  },
  
  resourceBudget: {
    projectId: 'p3',
    summary: {
      totalBudget: 600,
      totalActual: 183,
      totalRemaining: 417,
      progressPercent: 30.5,
      billablePercent: 95.0,
      targetBillablePercent: 90.0,
      totalBudgetCost: 78000,
      totalActualCost: 23790,
    },
    byRole: [
      {
        id: 'role1',
        role: 'Senior Consultant',
        budgetHours: 200,
        actualHours: 65,
        remainingHours: 135,
        percentUsed: 32.5,
        billRate: 160,
        budgetCost: 32000,
        actualCost: 10400,
      },
      {
        id: 'role2',
        role: 'Consultant',
        budgetHours: 250,
        actualHours: 78,
        remainingHours: 172,
        percentUsed: 31.2,
        billRate: 120,
        budgetCost: 30000,
        actualCost: 9360,
      },
      {
        id: 'role3',
        role: 'Developer',
        budgetHours: 150,
        actualHours: 40,
        remainingHours: 110,
        percentUsed: 26.7,
        billRate: 125,
        budgetCost: 18750,
        actualCost: 5000,
      },
    ],
    byPerson: [
      {
        id: 'person1',
        personId: 'e3',
        personName: 'Catherine Davies',
        role: 'Senior Consultant',
        budgetHours: 200,
        actualHours: 65,
        utilisation: 32.5,
        costRate: 120,
        budgetCost: 24000,
        actualCost: 7800,
      },
      {
        id: 'person2',
        personId: 'e6',
        personName: 'Frank Brown',
        role: 'Consultant',
        budgetHours: 150,
        actualHours: 48,
        utilisation: 32.0,
        costRate: 100,
        budgetCost: 15000,
        actualCost: 4800,
      },
      {
        id: 'person3',
        personId: 'e11',
        personName: 'Katie White',
        role: 'Consultant',
        budgetHours: 100,
        actualHours: 30,
        utilisation: 30.0,
        costRate: 80,
        budgetCost: 8000,
        actualCost: 2400,
      },
      {
        id: 'person4',
        personId: 'e12',
        personName: 'Liam Harris',
        role: 'Developer',
        budgetHours: 150,
        actualHours: 40,
        utilisation: 26.7,
        costRate: 80,
        budgetCost: 12000,
        actualCost: 3200,
      },
    ],
    byPhase: [],
  },
  
  allBudgets: {
    projectId: 'p3',
    profitability: {
      revenue: {
        budget: 96000,
        actual: 32350,
        variance: 63650,
      },
      directCosts: {
        budget: 58000,
        actual: 19000,
        variance: 39000,
      },
      grossProfit: {
        budget: 38000,
        actual: 13350,
        variance: 24650,
      },
      grossMargin: {
        budget: 39.6,
        actual: 41.3,
        variance: 1.7,
      },
      overhead: {
        budget: 2000,
        actual: 500,
        variance: 1500,
      },
      netProfit: {
        budget: 36000,
        actual: 12850,
        variance: 23150,
      },
      netMargin: {
        budget: 37.5,
        actual: 39.7,
        variance: 2.2,
      },
    },
    burnRateData: generateBurnRateData(60000, 19500, 0.33, 4),
    forecast: {
      projectedFinalRevenue: 96000,
      projectedFinalCost: 58500,
      projectedFinalMargin: 39.1,
      revenueVariance: 0,
      costVariance: -1500,
      marginVariance: 1.6,
    },
  },
  
  alerts: [],
  
  revisions: [
    {
      id: 'rev1',
      projectId: 'p3',
      revisionNumber: 1,
      date: '2024-09-01',
      changedBy: 'e1',
      changedByName: 'Alice Johnson',
      reason: 'Initial retainer agreement',
      status: 'current',
      costBudget: 60000,
      salesBudget: 96000,
      resourceBudget: 600,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: '2024-09-02',
    },
  ],
  
  lastUpdated: '2024-12-15T16:45:00Z',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function generateBurnRateData(
  totalBudget: number,
  currentActual: number,
  progressPercent: number,
  monthsElapsed: number
): BurnRateDataPoint[] {
  const data: BurnRateDataPoint[] = [];
  const totalMonths = Math.ceil(monthsElapsed / progressPercent);
  const monthlyBudget = totalBudget / totalMonths;
  const monthlyActual = currentActual / monthsElapsed;
  
  for (let i = 0; i <= totalMonths; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - monthsElapsed + i);
    
    data.push({
      date: date.toISOString().split('T')[0],
      budgetCumulative: Math.min(monthlyBudget * i, totalBudget),
      actualCumulative: i <= monthsElapsed ? monthlyActual * i : 0,
      forecastCumulative: i <= monthsElapsed ? 0 : monthlyActual * i,
    });
  }
  
  return data;
}

// ============================================================================
// DYNAMIC BUDGET GENERATION FOR ALL PROJECTS
// ============================================================================

// Import project data to generate realistic budgets
import { mockProjects } from '../mock-data';

function generateBudgetForProject(project: any): ProjectBudgetData {
  const totalBudget = project.budgetLabour + project.budgetExpense;
  const totalActual = project.actualLabour + project.actualExpense;
  const progress = project.completionPercent / 100;
  
  // Calculate budget metrics
  const budgetedMargin = ((project.budgetRevenue - totalBudget) / project.budgetRevenue) * 100;
  const actualMargin = project.actualRevenue > 0 ? ((project.actualRevenue - totalActual) / project.actualRevenue) * 100 : 0;
  const marginVariance = actualMargin - budgetedMargin;
  
  // Determine status based on health
  const getStatus = (health: string, variance: number) => {
    if (health === 'red' || variance > 15) return 'red';
    if (health === 'amber' || variance > 5) return 'amber';
    return 'green';
  };
  
  // Generate cost budget categories
  const consultingBudget = project.budgetLabour * 0.4;
  const consultingActual = project.actualLabour * 0.42;
  const developmentBudget = project.budgetLabour * 0.35;
  const developmentActual = project.actualLabour * 0.36;
  const projectMgmtBudget = project.budgetLabour * 0.15;
  const projectMgmtActual = project.actualLabour * 0.14;
  const qaTestingBudget = project.budgetLabour * 0.10;
  const qaTestingActual = project.actualLabour * 0.08;
  
  const travelBudget = project.budgetExpense * 0.4;
  const travelActual = project.actualExpense * 0.45;
  const softwareBudget = project.budgetExpense * 0.35;
  const softwareActual = project.actualExpense * 0.32;
  const hardwareBudget = project.budgetExpense * 0.15;
  const hardwareActual = project.actualExpense * 0.15;
  const otherBudget = project.budgetExpense * 0.10;
  const otherActual = project.actualExpense * 0.08;
  
  const costBudget: CostBudget = {
    projectId: project.id,
    categories: [
      {
        id: 'cat1',
        name: 'Consulting',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-1',
        budget: consultingBudget,
        actual: consultingActual,
        committed: 0,
        available: consultingBudget - consultingActual,
        progressPercent: (consultingActual / consultingBudget) * 100,
        subcategories: [
          {
            id: 'sub1',
            name: 'Strategy Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-1',
            budget: consultingBudget * 0.5,
            actual: consultingActual * 0.52,
            committed: 0,
            available: consultingBudget * 0.5 - consultingActual * 0.52,
            progressPercent: (consultingActual * 0.52 / (consultingBudget * 0.5)) * 100,
          },
          {
            id: 'sub2',
            name: 'Technical Consulting',
            scope: 'global',
            globalSubcategoryId: 'gs-2',
            budget: consultingBudget * 0.35,
            actual: consultingActual * 0.33,
            committed: 0,
            available: consultingBudget * 0.35 - consultingActual * 0.33,
            progressPercent: (consultingActual * 0.33 / (consultingBudget * 0.35)) * 100,
          },
          {
            id: 'sub3',
            name: 'Business Analysis',
            scope: 'global',
            globalSubcategoryId: 'gs-3',
            budget: consultingBudget * 0.15,
            actual: consultingActual * 0.15,
            committed: 0,
            available: consultingBudget * 0.15 - consultingActual * 0.15,
            progressPercent: (consultingActual * 0.15 / (consultingBudget * 0.15)) * 100,
          },
        ],
      },
      {
        id: 'cat2',
        name: 'Development',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-2',
        budget: developmentBudget,
        actual: developmentActual,
        committed: 0,
        available: developmentBudget - developmentActual,
        progressPercent: (developmentActual / developmentBudget) * 100,
        subcategories: [
          {
            id: 'sub4',
            name: 'Frontend Development',
            scope: 'global',
            globalSubcategoryId: 'gs-4',
            budget: developmentBudget * 0.5,
            actual: developmentActual * 0.48,
            committed: 0,
            available: developmentBudget * 0.5 - developmentActual * 0.48,
            progressPercent: (developmentActual * 0.48 / (developmentBudget * 0.5)) * 100,
          },
          {
            id: 'sub5',
            name: 'Backend Development',
            scope: 'global',
            globalSubcategoryId: 'gs-5',
            budget: developmentBudget * 0.5,
            actual: developmentActual * 0.52,
            committed: 0,
            available: developmentBudget * 0.5 - developmentActual * 0.52,
            progressPercent: (developmentActual * 0.52 / (developmentBudget * 0.5)) * 100,
          },
        ],
      },
      {
        id: 'cat3',
        name: 'Project Management',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-3',
        budget: projectMgmtBudget,
        actual: projectMgmtActual,
        committed: 0,
        available: projectMgmtBudget - projectMgmtActual,
        progressPercent: (projectMgmtActual / projectMgmtBudget) * 100,
        subcategories: [],
      },
      {
        id: 'cat4',
        name: 'QA & Testing',
        type: 'labour',
        scope: 'global',
        globalCategoryId: 'gc-4',
        budget: qaTestingBudget,
        actual: qaTestingActual,
        committed: 0,
        available: qaTestingBudget - qaTestingActual,
        progressPercent: (qaTestingActual / qaTestingBudget) * 100,
        subcategories: [],
      },
      {
        id: 'cat5',
        name: 'Travel & Accommodation',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-5',
        budget: travelBudget,
        actual: travelActual,
        committed: 0,
        available: travelBudget - travelActual,
        progressPercent: (travelActual / travelBudget) * 100,
        subcategories: [],
      },
      {
        id: 'cat6',
        name: 'Software & Licenses',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-6',
        budget: softwareBudget,
        actual: softwareActual,
        committed: 0,
        available: softwareBudget - softwareActual,
        progressPercent: (softwareActual / softwareBudget) * 100,
        subcategories: [],
      },
      {
        id: 'cat7',
        name: 'Hardware & Equipment',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-7',
        budget: hardwareBudget,
        actual: hardwareActual,
        committed: 0,
        available: hardwareBudget - hardwareActual,
        progressPercent: (hardwareActual / hardwareBudget) * 100,
        subcategories: [],
      },
      {
        id: 'cat8',
        name: 'Other Expenses',
        type: 'expenses',
        scope: 'global',
        globalCategoryId: 'gc-8',
        budget: otherBudget,
        actual: otherActual,
        committed: 0,
        available: otherBudget - otherActual,
        progressPercent: (otherActual / otherBudget) * 100,
        subcategories: [],
      },
    ],
    phases: [],
    totals: {
      budget: totalBudget,
      actual: totalActual,
      committed: 0,
      available: totalBudget - totalActual,
      progressPercent: (totalActual / totalBudget) * 100,
    },
  };
  
  // Generate sales budget (for all project types)
  let salesBudget: SalesBudget;
  if (project.type === 'fixed-price' || project.type === 'milestone-based') {
    const invoiced = project.actualRevenue - (project.unbilledAmount || 0);
    const recognised = project.actualRevenue * 0.75;
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      contractValue: project.budgetRevenue,
      invoiced,
      recognised,
      remaining: project.budgetRevenue - invoiced,
      milestones: [],
      revenueRecognition: {
        method: 'percentage-of-completion',
        percentComplete: progress * 100,
        revenueToRecognise: project.budgetRevenue * progress,
        revenueRecognised: recognised,
        deferredRevenue: invoiced - recognised,
        unbilledRevenue: project.unbilledAmount || 0,
      },
      phaseRevenue: [],
    };
  } else if (project.type === 'time-and-materials') {
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      estimated: project.budgetRevenue,
      actual: project.actualRevenue,
      unbilledTime: project.unbilledAmount || 0,
      unbilledExpenses: 0,
      streams: [],
      phaseRevenue: [],
    };
  } else if (project.type === 'retainer') {
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      retainer: {
        monthlyAmount: project.budgetRevenue / 12,
        contractMonths: 12,
        totalContract: project.budgetRevenue,
        invoiced: project.actualRevenue,
        remaining: project.budgetRevenue - project.actualRevenue,
        currentMonth: {
          hoursIncluded: 40,
          hoursUsed: 32,
          hoursRemaining: 8,
          overageRate: 150,
          progressPercent: 80,
        },
        rolloverPolicy: 'none',
        monthlyBreakdown: [],
      },
      phaseRevenue: [],
    };
  } else {
    // Default fallback for any other project type
    salesBudget = {
      projectId: project.id,
      projectType: project.type,
      estimated: project.budgetRevenue,
      actual: project.actualRevenue,
      phaseRevenue: [],
    };
  }
  
  // Generate resource budget with realistic rates and calculations
  const pmBudgetHours = project.budgetHours * 0.15;
  const pmActualHours = project.actualHours * 0.14;
  const pmRate = 200;
  
  const consultantBudgetHours = project.budgetHours * 0.25;
  const consultantActualHours = project.actualHours * 0.26;
  const consultantRate = 180;
  
  const developerBudgetHours = project.budgetHours * 0.40;
  const developerActualHours = project.actualHours * 0.42;
  const developerRate = 150;
  
  const qaBudgetHours = project.budgetHours * 0.20;
  const qaActualHours = project.actualHours * 0.18;
  const qaRate = 125;
  
  // Calculate total budget and actual costs
  const totalBudgetCost = (pmBudgetHours * pmRate) + (consultantBudgetHours * consultantRate) + 
                          (developerBudgetHours * developerRate) + (qaBudgetHours * qaRate);
  const totalActualCost = (pmActualHours * pmRate) + (consultantActualHours * consultantRate) + 
                          (developerActualHours * developerRate) + (qaActualHours * qaRate);
  
  const resourceBudget: ResourceBudget = {
    projectId: project.id,
    summary: {
      totalBudget: project.budgetHours,
      totalActual: project.actualHours,
      totalRemaining: project.budgetHours - project.actualHours,
      progressPercent: (project.actualHours / project.budgetHours) * 100,
      billablePercent: 85,
      targetBillablePercent: 80,
      totalBudgetCost: totalBudgetCost,
      totalActualCost: totalActualCost,
    },
    byRole: [
      {
        id: 'role1',
        role: 'Project Manager',
        budgetHours: pmBudgetHours,
        actualHours: pmActualHours,
        remainingHours: pmBudgetHours - pmActualHours,
        percentUsed: (pmActualHours / pmBudgetHours) * 100,
        billRate: pmRate,
        budgetCost: pmBudgetHours * pmRate,
        actualCost: pmActualHours * pmRate,
      },
      {
        id: 'role2',
        role: 'Senior Consultant',
        budgetHours: consultantBudgetHours,
        actualHours: consultantActualHours,
        remainingHours: consultantBudgetHours - consultantActualHours,
        percentUsed: (consultantActualHours / consultantBudgetHours) * 100,
        billRate: consultantRate,
        budgetCost: consultantBudgetHours * consultantRate,
        actualCost: consultantActualHours * consultantRate,
      },
      {
        id: 'role3',
        role: 'Developer',
        budgetHours: developerBudgetHours,
        actualHours: developerActualHours,
        remainingHours: developerBudgetHours - developerActualHours,
        percentUsed: (developerActualHours / developerBudgetHours) * 100,
        billRate: developerRate,
        budgetCost: developerBudgetHours * developerRate,
        actualCost: developerActualHours * developerRate,
      },
      {
        id: 'role4',
        role: 'QA Analyst',
        budgetHours: qaBudgetHours,
        actualHours: qaActualHours,
        remainingHours: qaBudgetHours - qaActualHours,
        percentUsed: (qaActualHours / qaBudgetHours) * 100,
        billRate: qaRate,
        budgetCost: qaBudgetHours * qaRate,
        actualCost: qaActualHours * qaRate,
      },
    ],
    byPerson: [
      {
        id: 'person1',
        personId: 'p1',
        personName: 'Sarah Johnson',
        role: 'Project Manager',
        budgetHours: pmBudgetHours,
        actualHours: pmActualHours,
        utilisation: (pmActualHours / pmBudgetHours) * 100,
        costRate: pmRate,
        budgetCost: pmBudgetHours * pmRate,
        actualCost: pmActualHours * pmRate,
      },
      {
        id: 'person2',
        personId: 'p2',
        personName: 'Michael Chen',
        role: 'Senior Consultant',
        budgetHours: consultantBudgetHours * 0.6,
        actualHours: consultantActualHours * 0.6,
        utilisation: (consultantActualHours * 0.6 / (consultantBudgetHours * 0.6)) * 100,
        costRate: consultantRate,
        budgetCost: consultantBudgetHours * 0.6 * consultantRate,
        actualCost: consultantActualHours * 0.6 * consultantRate,
      },
      {
        id: 'person3',
        personId: 'p3',
        personName: 'Emily Rodriguez',
        role: 'Senior Consultant',
        budgetHours: consultantBudgetHours * 0.4,
        actualHours: consultantActualHours * 0.4,
        utilisation: (consultantActualHours * 0.4 / (consultantBudgetHours * 0.4)) * 100,
        costRate: consultantRate,
        budgetCost: consultantBudgetHours * 0.4 * consultantRate,
        actualCost: consultantActualHours * 0.4 * consultantRate,
      },
      {
        id: 'person4',
        personId: 'p4',
        personName: 'David Kim',
        role: 'Developer',
        budgetHours: developerBudgetHours * 0.5,
        actualHours: developerActualHours * 0.5,
        utilisation: (developerActualHours * 0.5 / (developerBudgetHours * 0.5)) * 100,
        costRate: developerRate,
        budgetCost: developerBudgetHours * 0.5 * developerRate,
        actualCost: developerActualHours * 0.5 * developerRate,
      },
      {
        id: 'person5',
        personId: 'p5',
        personName: 'Jessica Taylor',
        role: 'Developer',
        budgetHours: developerBudgetHours * 0.5,
        actualHours: developerActualHours * 0.5,
        utilisation: (developerActualHours * 0.5 / (developerBudgetHours * 0.5)) * 100,
        costRate: developerRate,
        budgetCost: developerBudgetHours * 0.5 * developerRate,
        actualCost: developerActualHours * 0.5 * developerRate,
      },
      {
        id: 'person6',
        personId: 'p6',
        personName: 'Alex Martinez',
        role: 'QA Analyst',
        budgetHours: qaBudgetHours,
        actualHours: qaActualHours,
        utilisation: (qaActualHours / qaBudgetHours) * 100,
        costRate: qaRate,
        budgetCost: qaBudgetHours * qaRate,
        actualCost: qaActualHours * qaRate,
      },
    ],
    byPhase: [
      {
        phaseId: 'phase1',
        phaseName: 'Discovery & Planning',
        budgetHours: project.budgetHours * 0.15,
        actualHours: project.actualHours * 0.18,
        remainingHours: Math.max(0, project.budgetHours * 0.15 - project.actualHours * 0.18),
        status: 'complete',
        startDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 'phase2',
        phaseName: 'Design',
        budgetHours: project.budgetHours * 0.20,
        actualHours: project.actualHours * 0.22,
        remainingHours: Math.max(0, project.budgetHours * 0.20 - project.actualHours * 0.22),
        status: 'complete',
        startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 'phase3',
        phaseName: 'Development',
        budgetHours: project.budgetHours * 0.45,
        actualHours: project.actualHours * 0.42,
        remainingHours: Math.max(0, project.budgetHours * 0.45 - project.actualHours * 0.42),
        status: 'in-progress',
        startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 'phase4',
        phaseName: 'Testing & QA',
        budgetHours: project.budgetHours * 0.15,
        actualHours: project.actualHours * 0.12,
        remainingHours: Math.max(0, project.budgetHours * 0.15 - project.actualHours * 0.12),
        status: 'in-progress',
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      {
        phaseId: 'phase5',
        phaseName: 'Deployment & Handover',
        budgetHours: project.budgetHours * 0.05,
        actualHours: 0,
        remainingHours: project.budgetHours * 0.05,
        status: 'not-started',
      },
    ],
  };
  
  // Generate burn rate data (must be before allBudgetsView)
  const monthsElapsed = Math.max(1, Math.floor(progress * 12));
  const burnRateData = generateBurnRateData(totalBudget, totalActual, progress, monthsElapsed);
  
  // Generate forecast (must be before allBudgetsView)
  const projectedFinalCost = progress > 0 ? totalActual / progress : totalBudget;
  const projectedFinalRevenue = progress > 0 ? project.actualRevenue / progress : project.budgetRevenue;
  const projectedFinalMargin = projectedFinalRevenue > 0 ? ((projectedFinalRevenue - projectedFinalCost) / projectedFinalRevenue) * 100 : 0;
  
  // Generate all budgets view
  const allBudgetsView: AllBudgetsView = {
    projectId: project.id,
    profitability: {
      revenue: {
        budget: project.budgetRevenue,
        actual: project.actualRevenue,
        variance: project.actualRevenue - project.budgetRevenue,
      },
      directCosts: {
        budget: totalBudget,
        actual: totalActual,
        variance: totalActual - totalBudget,
      },
      grossProfit: {
        budget: project.budgetRevenue - totalBudget,
        actual: project.actualRevenue - totalActual,
        variance: (project.actualRevenue - totalActual) - (project.budgetRevenue - totalBudget),
      },
      grossMargin: {
        budget: budgetedMargin,
        actual: actualMargin,
        variance: marginVariance,
      },
      overhead: {
        budget: totalBudget * 0.05,
        actual: totalActual * 0.05,
        variance: totalActual * 0.05 - totalBudget * 0.05,
      },
      netProfit: {
        budget: (project.budgetRevenue - totalBudget) * 0.95,
        actual: (project.actualRevenue - totalActual) * 0.95,
        variance: (project.actualRevenue - totalActual) * 0.95 - (project.budgetRevenue - totalBudget) * 0.95,
      },
      netMargin: {
        budget: budgetedMargin * 0.95,
        actual: actualMargin * 0.95,
        variance: (actualMargin - budgetedMargin) * 0.95,
      },
    },
    burnRateData,
    forecast: {
      projectedFinalRevenue,
      projectedFinalCost,
      projectedFinalMargin,
      revenueVariance: projectedFinalRevenue - project.budgetRevenue,
      costVariance: projectedFinalCost - totalBudget,
      marginVariance: projectedFinalMargin - budgetedMargin,
      warnings: [],
    },
  };
  
  // Generate alerts based on project health
  const alerts: BudgetAlert[] = [];
  if (project.health === 'red') {
    alerts.push({
      id: `alert-${project.id}-1`,
      projectId: project.id,
      type: 'cost-over-budget',
      severity: 'critical',
      title: 'Cost Budget Exceeded',
      description: `Cost budget significantly exceeded - ${((totalActual / totalBudget - 1) * 100).toFixed(1)}% over budget`,
      message: `Cost budget significantly exceeded - ${((totalActual / totalBudget - 1) * 100).toFixed(1)}% over budget`,
      createdAt: new Date().toISOString(),
      dismissed: false,
    });
    alerts.push({
      id: `alert-${project.id}-2`,
      projectId: project.id,
      type: 'margin-erosion',
      severity: 'warning',
      title: 'Margin Decline',
      description: `Margin has declined from ${budgetedMargin.toFixed(1)}% to ${actualMargin.toFixed(1)}%`,
      message: `Margin has declined from ${budgetedMargin.toFixed(1)}% to ${actualMargin.toFixed(1)}%`,
      createdAt: new Date().toISOString(),
      dismissed: false,
    });
  } else if (project.health === 'amber') {
    alerts.push({
      id: `alert-${project.id}-1`,
      projectId: project.id,
      type: 'cost-warning',
      severity: 'warning',
      title: 'Cost Warning',
      description: `Cost tracking ${((totalActual / totalBudget) * 100).toFixed(1)}% of budget with ${project.completionPercent}% completion`,
      message: `Cost tracking ${((totalActual / totalBudget) * 100).toFixed(1)}% of budget with ${project.completionPercent}% completion`,
      createdAt: new Date().toISOString(),
      dismissed: false,
    });
  }
  
  // Generate budget revisions
  const revisions: BudgetRevision[] = [
    {
      id: `rev-${project.id}-1`,
      projectId: project.id,
      revisionNumber: 1,
      date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      changedBy: 'e1',
      changedByName: 'Alice Johnson',
      reason: 'Initial budget approval',
      status: 'approved',
      costBudget: totalBudget,
      salesBudget: project.budgetRevenue,
      resourceBudget: project.budgetHours,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: new Date(Date.now() - 89 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    },
  ];
  
  if (project.health === 'amber' || project.health === 'red') {
    revisions.push({
      id: `rev-${project.id}-2`,
      projectId: project.id,
      revisionNumber: 2,
      date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      changedBy: 'e2',
      changedByName: 'Bob Smith',
      reason: 'Budget adjustment due to scope changes',
      status: 'current',
      costBudget: totalBudget * 1.1,
      salesBudget: project.budgetRevenue * 1.05,
      resourceBudget: project.budgetHours * 1.08,
      approvedBy: 'e18',
      approvedByName: 'Rachel King',
      approvedDate: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
  }
  
  // Build summary
  const summary: BudgetSummary = {
    revenue: {
      budget: project.budgetRevenue,
      actual: project.actualRevenue,
      variance: project.actualRevenue - project.budgetRevenue,
      variancePercent: ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100,
      progressPercent: (project.actualRevenue / project.budgetRevenue) * 100,
      trend: project.actualRevenue > project.budgetRevenue * progress ? 'up' : 'down',
      status: getStatus(project.health, Math.abs((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100),
    },
    costs: {
      budget: totalBudget,
      actual: totalActual,
      variance: totalBudget - totalActual,
      variancePercent: ((totalBudget - totalActual) / totalBudget) * 100,
      progressPercent: (totalActual / totalBudget) * 100,
      trend: totalActual > totalBudget * progress ? 'up' : 'down',
      status: getStatus(project.health, Math.abs((totalActual - totalBudget * progress) / totalBudget) * 100),
    },
    margin: {
      budgetedMargin,
      actualMargin,
      variance: marginVariance,
      status: getStatus(project.health, Math.abs(marginVariance)),
    },
    hours: {
      budget: project.budgetHours,
      actual: project.actualHours,
      remaining: project.budgetHours - project.actualHours,
      progressPercent: (project.actualHours / project.budgetHours) * 100,
      status: getStatus(project.health, Math.abs((project.actualHours / project.budgetHours - progress) * 100)),
    },
    health: {
      score: project.health,
      statusText: project.health === 'green' ? 'On Track' : project.health === 'amber' ? 'Requires Attention' : 'Critical',
      factors: alerts.map(a => a.message),
    },
  };
  
  return {
    projectId: project.id,
    projectName: project.name,
    projectType: project.type,
    summary,
    costBudget,
    salesBudget,
    resourceBudget,
    allBudgets: allBudgetsView,
    alerts,
    revisions,
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export const mockBudgetData: Record<string, ProjectBudgetData> = {
  p1: fixedPriceProject,
  p2: timeAndMaterialsProject,
  p3: retainerProject,
};

export function getBudgetDataByProjectId(projectId: string): ProjectBudgetData | undefined {
  // If we have specific mock data for this project, return it
  if (mockBudgetData[projectId]) {
    return mockBudgetData[projectId];
  }
  
  // Find the project in mockProjects
  const project = mockProjects.find(p => p.id === projectId);
  if (!project) {
    return undefined;
  }
  
  // Generate budget data dynamically
  return generateBudgetForProject(project);
}

export function getAllBudgetData(): ProjectBudgetData[] {
  // Return all projects with budget data
  return mockProjects.map(project => {
    // Use existing mock data if available
    if (mockBudgetData[project.id]) {
      return mockBudgetData[project.id];
    }
    // Otherwise generate it
    return generateBudgetForProject(project);
  });
}
